/* eslint-disable no-underscore-dangle */
/* eslint-disable prefer-destructuring */
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FlexLayoutModule } from '@angular/flex-layout';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { PatientService } from '@sondermind/data-access/patient';
import {
  ClientReferralHttpService,
  ClientReferralMemberService,
  IProviderInvitedReferralRequestBody
} from '@sondermind/data-access/provider-facing/client-referrals';
import { BreakpointsService } from '@sondermind/utilities/services';
import { IrisButtonContainerAlignments, IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisFormFieldModule } from '@sondermind/iris/form-field';
import { IrisRadioGroupModule, RadioOption } from '@sondermind/iris/radio-group';
import { IrisTextInputModule } from '@sondermind/iris/text-input';
import { ServerResponse } from '@sondermind/utilities/models-http';
import { Patient } from '@sondermind/utilities/models-patient';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { AtLeastOneValidator } from '@sondermind/utilities/tools/validators/at-least-one.validator';
import { EmailValidator } from '@sondermind/utilities/tools/validators/email.validator';

@Component({
  selector: 'calendar-embedded-client-creator',
  templateUrl: './embedded-client-creator.component.html',
  styleUrls: ['./embedded-client-creator.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    IrisFormFieldModule,
    IrisTextInputModule,
    IrisRadioGroupModule,
    IrisButtonModule
  ]
})
export class EmbeddedClientCreatorComponent implements OnInit, OnDestroy {
  @Input() user: ProviderUser = new ProviderUser();
  @Input() cancelable: boolean = true;

  saving = false;
  private defaultFormValue: any = null;

  form: UntypedFormGroup = null;

  @Output() patientCreated = new EventEmitter<Patient>();
  @Output() canceled = new EventEmitter<boolean>();

  irisButtonTypes = IrisButtonTypes;
  irisButtonContainerAlignments = IrisButtonContainerAlignments;

  intakeRequiredOptions: Array<RadioOption<boolean>> = [
    { name: 'Yes', value: true },
    { name: 'No', value: false }
  ];

  isMobile$ = this._breakpoint.isMobile$;

  private destroyed$ = new EventEmitter<void>();

  constructor(
    private _patientService: PatientService,
    private formBuilder: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private _breakpoint: BreakpointsService,
    private clientReferralService: ClientReferralHttpService,
    private clientReferralMemberService: ClientReferralMemberService
  ) { }

  handleSave(event: any): void {
    if (this.form.invalid) { return }

    this.saving = true;

    this.createProviderInvitedReferral$().subscribe({
      next: (resp) => {
        this.success(resp);
      },
      error: (err) => {
        this.error(err);
      }
    });
  }

  private createProviderInvitedReferral$(): Observable<ServerResponse<Patient, any>> {
    const data: IProviderInvitedReferralRequestBody = {
      first_name: this.form.value.firstName,
      last_name: this.form.value.lastName,
      email: this.form.value.email,
      intake_required: this.form.value.intakeRequired
    };

    return this.clientReferralService.createProviderInvitedReferral$(data).pipe(
      switchMap((referralResp) => {
        const referralMemberId = referralResp.data.clientReferralMemberIds[0];
        return this.clientReferralMemberService.getMember$(referralMemberId).pipe(
          switchMap((memberResp) => this._patientService.load(memberResp.data.patientId))
        );
      })
    );
  }

  success(response: ServerResponse<Patient, any>): void {
    this.patientCreated.emit(response.value);
    this.reset();
    this.saving = false;
  }

  error(error: any): void {
    this.snackBar.open('Sorry something went wrong', 'Dismiss');
    // eslint-disable-next-line no-console
    console.error(error);
    this.saving = false;
  }

  handleCancel(event: any): void {
    this.reset();
    this.canceled.emit(true);
  }

  ngOnInit(): void {
    this.form = this.buildForm();

    // save off default for when we reset
    this.defaultFormValue = this.form.value;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  get valid(): boolean {
    return this.form.valid;
  }

  /**
   * Get form-level error message if present
   * Used to display form-level validation errors (e.g., from AtLeastOneValidator)
   */
  get formLevelError(): string | null {
    if (!this.form?.errors || !this.form.dirty) {
      return null;
    }

    const errors = this.form.errors;
    // The AtLeastOneValidator creates an error with key 'base'
    if (errors['base']) {
      const baseError = errors['base'];
      // If it's a string, return it directly; otherwise return the default message
      return typeof baseError === 'string' ? baseError : 'A first or last name is required!';
    }

    return null;
  }

  reset(): void {
    this.form.reset(this.defaultFormValue);
  }

  /**
   * Build form according to the invite client variant.
   */
  private buildForm(): UntypedFormGroup {
    const compose = Validators.compose;
    const { required } = Validators;

    const formControls = {
      firstName: [null, required],
      lastName: [null, required],
      email: [null, compose([EmailValidator.format, required])],
      intakeRequired: [true, required],
    };

    const validators = [
      AtLeastOneValidator.of(['firstName', 'lastName'], { base: 'A first or last name is required!' })
    ];

    const formGroup: UntypedFormGroup = this.formBuilder.group(
      formControls,
      {
        validator: compose(
          validators
        )
      }
    );

    return formGroup;
  }
}
